import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core';
import { ActionActivityService, ActionCapture } from './action-activity.service';
import { loadingLabelFits } from './loading-label-fit';
import {
  GENERIC_PROGRESS_LABEL,
  progressLabel,
} from './progress-label';

/** Marks the element the user is currently waiting on. */
const BUSY_ATTRIBUTE = 'data-arm-action-busy';

/** Marks the spinner and label this service adds inside that element. */
const CONTENT_ATTRIBUTE = 'data-arm-action-busy-content';

/** Opt out for a control that must never take a loading state. */
const OPT_OUT_ATTRIBUTE = 'data-action-feedback';

/** Controls a click can put into a loading state. */
const ACTION_SELECTOR = 'button, a, [role="button"], [role="menuitem"]';

/**
 * Anything still busy after this long is assumed to have lost its ending,
 * so the control is handed back to the user rather than left spinning.
 */
const MAX_BUSY_MS = 30_000;

const STYLE_ID = 'arm-action-feedback-styles';

const STYLES = `
[${BUSY_ATTRIBUTE}] {
  cursor: progress !important;
  pointer-events: none !important;
  font-size: 0 !important;
}
[${BUSY_ATTRIBUTE}] > *:not([${CONTENT_ATTRIBUTE}]) {
  display: none !important;
}
[${CONTENT_ATTRIBUTE}] {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  line-height: 1.25;
}
[${CONTENT_ATTRIBUTE}] .arm-action-spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  flex: none;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 9999px;
  animation: arm-action-spin 0.6s linear infinite;
}
@keyframes arm-action-spin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  [${CONTENT_ATTRIBUTE}] .arm-action-spinner { animation-duration: 2.4s; }
}
`;

interface BusyElementState {
  readonly element: HTMLElement;
  readonly content: HTMLElement;
  readonly minWidth: string;
  readonly minHeight: string;
  readonly fontSize: string;
  readonly ariaBusy: string | null;
  readonly wasDisabled: boolean;
  readonly timer: ReturnType<typeof setTimeout>;
}

/**
 * Puts the control the user actually clicked into a loading state.
 *
 * The click is caught once, on the document, so this works for every control
 * in the application — a table's row action, a dropdown item, a modal footer
 * button, a bulk action, a button rendered a second ago — without any screen
 * opting in. The control is matched to the work its click started through
 * {@link ActionActivityService}, which is also what drives the global
 * progress bar.
 *
 * Controls that render their own loading state, such as the design system
 * button, are left to do so.
 */
@Injectable({ providedIn: 'root' })
export class ActionFeedbackService {
  private readonly activity = inject(ActionActivityService);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  private readonly busy = new Map<HTMLElement, BusyElementState>();
  private installed = false;

  /** Start listening for clicks. Call once, from the root component. */
  install(): void {
    if (this.installed) {
      return;
    }
    this.installed = true;

    this.addStyles();

    // Outside Angular: a click on a control this service cannot act on must
    // not cost a change detection run, and the DOM it does touch is its own.
    this.zone.runOutsideAngular(() => {
      this.document.addEventListener(
        'click',
        event => this.onClick(event as MouseEvent),
        true
      );
    });
  }

  /** Hand every busy control back, for instance when the route changed. */
  releaseAll(): void {
    for (const element of [...this.busy.keys()]) {
      this.restore(element);
    }
  }

  private onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const element = target?.closest<HTMLElement>(ACTION_SELECTOR) ?? null;
    if (!element) {
      return;
    }

    // A control already waiting swallows the impatient second click: the
    // request is in flight and firing it twice is exactly what this prevents.
    if (this.busy.has(element)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!this.canTakeFeedback(element)) {
      return;
    }

    const capture = this.activity.capture();
    capture.watch(pending => this.onCaptureChange(element, capture, pending));
  }

  private onCaptureChange(
    element: HTMLElement,
    capture: ActionCapture,
    pending: boolean
  ): void {
    if (pending) {
      this.markBusy(element);
      return;
    }

    capture.close();
    this.restore(element);
  }

  private canTakeFeedback(element: HTMLElement): boolean {
    if (element.getAttribute(OPT_OUT_ATTRIBUTE) === 'off') {
      return false;
    }

    if (element.hasAttribute('disabled') || element.ariaDisabled === 'true') {
      return false;
    }

    // The design system button reports its own state, in its own template.
    if (element.closest('lib-button')) {
      return false;
    }

    // A link that navigates is reported by the global progress bar instead.
    return !this.isNavigationLink(element);
  }

  private isNavigationLink(element: HTMLElement): boolean {
    if (element.tagName !== 'A') {
      return false;
    }

    const href = element.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) {
      return false;
    }

    return true;
  }

  private markBusy(element: HTMLElement): void {
    if (this.busy.has(element)) {
      return;
    }

    const { width, height } = element.getBoundingClientRect();
    const wording = progressLabel(element.textContent?.trim() || undefined);
    const fitsGeneric =
      wording !== GENERIC_PROGRESS_LABEL ||
      loadingLabelFits(element, wording, width);
    const label = fitsGeneric ? wording : undefined;
    const content = this.buildContent(element, label);

    const state: BusyElementState = {
      element,
      content,
      minWidth: element.style.minWidth,
      minHeight: element.style.minHeight,
      fontSize: element.style.fontSize,
      ariaBusy: element.getAttribute('aria-busy'),
      wasDisabled: this.isFormControl(element) && element.disabled,
      timer: setTimeout(() => this.restore(element), MAX_BUSY_MS),
    };
    this.busy.set(element, state);

    // Keeping the measured box stops the control from collapsing around the
    // spinner; it may still grow for a longer word.
    if (width && height) {
      element.style.minWidth = `${Math.round(width)}px`;
      element.style.minHeight = `${Math.round(height)}px`;
    }

    element.setAttribute(BUSY_ATTRIBUTE, '');
    element.setAttribute('aria-busy', 'true');
    element.appendChild(content);

    if (this.isFormControl(element)) {
      element.disabled = true;
    }
  }

  private buildContent(
    element: HTMLElement,
    label: string | undefined
  ): HTMLElement {
    const content = this.document.createElement('span');
    content.setAttribute(CONTENT_ATTRIBUTE, '');
    content.style.fontSize = this.readableFontSize(element);

    const spinner = this.document.createElement('span');
    spinner.className = 'arm-action-spinner';
    spinner.setAttribute('aria-hidden', 'true');
    content.appendChild(spinner);

    if (label) {
      const text = this.document.createElement('span');
      text.textContent = label;
      content.appendChild(text);
    }

    return content;
  }

  /**
   * The element's own text is collapsed while it is busy, so the replacement
   * carries the size the control was already using.
   */
  private readableFontSize(element: HTMLElement): string {
    const view = this.document.defaultView;
    const size = view?.getComputedStyle(element).fontSize;

    return size && size !== '0px' ? size : '0.875rem';
  }

  private restore(element: HTMLElement): void {
    const state = this.busy.get(element);
    if (!state) {
      return;
    }

    this.busy.delete(element);
    clearTimeout(state.timer);

    state.content.remove();
    element.removeAttribute(BUSY_ATTRIBUTE);
    element.style.minWidth = state.minWidth;
    element.style.minHeight = state.minHeight;
    element.style.fontSize = state.fontSize;

    if (state.ariaBusy === null) {
      element.removeAttribute('aria-busy');
    } else {
      element.setAttribute('aria-busy', state.ariaBusy);
    }

    if (this.isFormControl(element)) {
      element.disabled = state.wasDisabled;
    }
  }

  private isFormControl(
    element: HTMLElement
  ): element is HTMLButtonElement | HTMLInputElement {
    return element.tagName === 'BUTTON' || element.tagName === 'INPUT';
  }

  private addStyles(): void {
    if (this.document.getElementById(STYLE_ID)) {
      return;
    }

    const style = this.document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    this.document.head.appendChild(style);
  }
}
